import {
  EntityLocationDTO,
  SalespersonFilterOption,
} from '@user/store/map/typings'
import { getVariantFromEntityType } from '@user/components/HereMap/utils/createMarker'
import { MapPinTypeVariant } from '@user/components/HereMap/MapPin/types'

// Pure helpers extracted from the salesperson-filter hooks/effect so they can
// be unit-tested without Redux/React (mirrors useMapLibreMarkers.utils.ts).

// Rule 1/2: default/"unfiltered" state is every option selected, never empty.
// Persisted IDs are validated against the current picklist and deduped —
// stale IDs (removed/reassigned salespeople) are dropped, and if that leaves
// nothing left, we fall back to "everyone selected" rather than an empty map.
export function computeInitialSelection(
  options: SalespersonFilterOption[],
  persistedValue: string | undefined,
): string[] {
  const validIds = new Set(options.map((o) => o.userId))
  if (persistedValue && persistedValue.trim() !== '') {
    const restored = [
      ...new Set(persistedValue.split('_').filter((id) => validIds.has(id))),
    ]
    if (restored.length > 0) return restored
  }
  return options.map((o) => o.userId)
}

// Rule 1/2/6: "everyone selected" == unfiltered state.
export function isEverythingSelected(
  selection: string[],
  options: SalespersonFilterOption[],
): boolean {
  return selection.length === options.length
}

// Rule 1/2: serialise the selection for persistence. The unfiltered "everyone
// selected" state must persist as an empty value, NOT an enumerated id list —
// otherwise onboarding a new salesperson later turns a stored full list into an
// accidental (N-1)-of-N filter for every existing user, since the grown option
// set makes the old ids a strict subset. computeInitialSelection restores an
// empty value back to "everyone", so the round-trip stays unfiltered.
export function serializeSelection(
  selection: string[],
  options: SalespersonFilterOption[],
): string {
  return isEverythingSelected(selection, options) ? '' : selection.join('_')
}

// Rules 3-5: a pin is hidden only when it has an assignee that is NOT in the
// current selection, unless it's exempt (multitenant/PestPac/Draft Lead), its
// assignee isn't in the picklist at all, or the filter is in its unfiltered
// ("everyone selected") state.
export function isPinVisibleForSalespersonFilter(
  loc: Pick<EntityLocationDTO, 'assigneeId' | 'multitenant' | 'entityType'>,
  selection: string[],
  options: SalespersonFilterOption[],
): boolean {
  if (loc.multitenant) return true // Rule 5
  const variant = getVariantFromEntityType(loc.entityType)
  if (
    variant === MapPinTypeVariant.PestPac ||
    variant === MapPinTypeVariant.DraftLead ||
    variant === MapPinTypeVariant.PendingLead
  )
    return true // Rule 4
  if (!loc.assigneeId) return true // Rule 3
  if (isEverythingSelected(selection, options)) return true // Rule 1/2
  // The fetch returns every tenant record in the bbox, but the picklist is a
  // narrower population (team-scoped, admins/inactive/no-map-activity excluded).
  // An assignee outside the picklist can never be selected, so treat those pins
  // like unassigned ones (always visible) instead of stranding them the moment
  // the selection narrows — otherwise the only way to see them again is to
  // re-select everyone.
  // NOTE (SH-2168 POC): scoping the fetch itself to the picklist population is
  // the more correct fix, but that changes what "everyone selected" returns and
  // should be designed as part of an overarching FilterDrawer change, not bolted
  // onto this salesperson-filter proof of concept.
  const isSelectableAssignee = options.some((o) => o.userId === loc.assigneeId)
  if (!isSelectableAssignee) return true
  return selection.includes(loc.assigneeId) // Rule 3
}

// Rule 6: badge shows a count only when the selection differs from "everyone."
export function computeBadgeCount(
  selection: string[],
  options: SalespersonFilterOption[],
): number | null {
  return isEverythingSelected(selection, options) ? null : selection.length
}
